import type { MediaKitContent } from "./types";
import { defaultLayout } from "./layout";
import { getMediaKitFolderPath } from "./mediaKitFolder";

/**
 * Media Kit images ONLY from brand Cloudinary folder `{brand}/mediakit`
 * (e.g. `delfuocampo/mediakit`). Never portfolio / gallery / random roots.
 *
 * Content is framed for brand / agency decision-makers hiring a model-creator
 * for social content production (Reels, UGC, Stories, photo).
 */
const MK = getMediaKitFolderPath(); // delfuocampo/mediakit

const HERO = `${MK}/hero-desfile`;
const PORTRAIT = `${MK}/portrait`;
const WORLD_1 = `${MK}/world-1`;
const WORLD_2 = `${MK}/world-2`;
const WORLD_3 = `${MK}/world-3`;
const WORLD_4 = `${MK}/world-4`;
const CONCEPT_1 = `${MK}/concept-1`;
const CONCEPT_2 = `${MK}/concept-2`;
const CONCEPT_3 = `${MK}/concept-3`;
const CONCEPT_4 = `${MK}/concept-4`;

const image = (publicId: string, alt: string) => ({ publicId, alt });

export const DEFAULT_MEDIA_KIT_SLUG = "delfu-ocampo";

/** Bump when public schema/copy framing changes — admin must save ≥ this for CMS to win over defaults */
export const MEDIA_KIT_CONTENT_VERSION = 3;

export const defaultContent: MediaKitContent = {
  id: "default",
  slug: DEFAULT_MEDIA_KIT_SLUG,
  status: "published",
  locale: "es-AR",
  version: MEDIA_KIT_CONTENT_VERSION,
  updatedAt: new Date().toISOString(),
  layout: defaultLayout(),
  brand: { name: "MODELTY", descriptor: "Talent & Creator Management" },
  profile: {
    name: "Delfu",
    surname: "Ocampo",
    descriptor:
      "Modelo y content creator en Buenos Aires. Colabora con marcas de moda, beauty y lifestyle en UGC, Reels y foto de producto — estética dark editorial, alto engagement.",
    bio: "Produce contenido propio con identidad visual consistente (luz moody, monocromo, tono de campaña). Trabaja con brief de marca, guión ligero y entrega lista para Instagram/TikTok, con opción de derechos para pauta (paid media).",
    location: "Buenos Aires, AR · LATAM + España",
    age: 22,
    instagram: {
      label: "@delfuocampo",
      href: "https://www.instagram.com/delfuocampo/",
      newTab: true,
    },
    email: {
      label: "hola@delfuocampo.com",
      href: "mailto:hola@delfuocampo.com?subject=Brief%20colaboraci%C3%B3n%20%2F%20UGC%20%2F%20Reels",
    },
  },
  hero: {
    kicker: "Media kit · Modelo & content creator",
    image: image(HERO, "Delfu Ocampo en desfile"),
  },
  about: {
    eyebrow: "01 — Sobre la creator",
    heading: "Por qué marcas",
    headingAccent: "la eligen.",
    paragraphs: [
      "Especializada en contenido para redes: Reels, Stories, UGC y foto de producto con estética dark editorial. Pensado para equipos de marketing y agencias que necesitan piezas listas para publicar o pautar.",
      "La audiencia responde con engagement alto: no es solo alcance, es atención (guardados, shares y visualizaciones por encima del tamaño de la cuenta).",
      "Producción desde Buenos Aires, brief en español, entrega en formatos nativos (9:16 / Stories) y plazos claros para one-shot o calendario mensual.",
    ],
    tags: [
      "Reels / video corto",
      "UGC para ads",
      "Pack Stories",
      "Moda · Beauty · Lifestyle",
      "Foto de producto",
      "Español · LATAM",
    ],
    image: image(PORTRAIT, "Retrato editorial de Delfu Ocampo"),
  },
  performance: {
    eyebrow: "02 — Resultados (stats)",
    heading: "Números para",
    headingAccent: "tomar la decisión.",
    periodLabel: "Referencia orgánica · últimos ~90 días",
    // Brand-decision KPIs — labels brands already use in media plans
    headlineMetrics: [
      {
        id: "engagement",
        value: "10.4%",
        label: "Engagement rate",
        hint: "Referencia industria moda/lifestyle ~1–3%",
        tone: "green",
        sortOrder: 10,
      },
      {
        id: "avg-views",
        value: "27K",
        label: "Vistas prom. / Reel",
        hint: "Alcance orgánico habitual por pieza",
        tone: "green",
        sortOrder: 20,
      },
      {
        id: "max-views",
        value: "116K",
        label: "Pico de vistas",
        hint: "Mejor Reel del período",
        tone: "green",
        sortOrder: 30,
      },
      {
        id: "campaigns",
        value: "10–15",
        label: "Entregas / mes",
        hint: "Capacidad según complejidad del brief",
        tone: "green",
        sortOrder: 40,
      },
    ],
    platformMetrics: [
      {
        id: "ig-followers",
        value: "9.1K",
        label: "Seguidores Instagram",
        tone: "gold",
        sortOrder: 10,
      },
      {
        id: "ig-avg",
        value: "27K",
        label: "Vistas prom. Reels IG",
        tone: "green",
        sortOrder: 20,
      },
      {
        id: "ig-best",
        value: "116K",
        label: "Mejor Reel Instagram",
        tone: "green",
        sortOrder: 30,
      },
      {
        id: "tt-followers",
        value: "1.3K",
        label: "Seguidores TikTok",
        tone: "gold",
        sortOrder: 40,
      },
      {
        id: "tt-avg",
        value: "6.6K",
        label: "Vistas prom. TikTok",
        tone: "green",
        sortOrder: 50,
      },
      {
        id: "tt-best",
        value: "14.8K",
        label: "Mejor video TikTok",
        tone: "green",
        sortOrder: 60,
      },
    ],
    noteTitle: "Cómo leerlo (equipos de marca)",
    noteBody:
      "Más que el tamaño de la cuenta, importan engagement y vistas por pieza. Un 10%+ de engagement y vistas muy por encima de los seguidores indica comunidad activa: ideal para UGC, Reels de producto y awareness con intención de marca.",
  },
  audience: {
    eyebrow: "03 — Audiencia (demographics)",
    heading: "A quién llega",
    headingAccent: "tu mensaje.",
    intro:
      "Mayormente mujeres 18–34, hispanohablantes (AR, MX, ES, CL). Buen fit para beauty, moda, joyería, skincare y lifestyle con tono dark/editorial — no audiencia genérica masiva.",
    age: [
      { id: "18-24", label: "18–24", value: 68, display: "68%", tone: "green", sortOrder: 10 },
      { id: "25-34", label: "25–34", value: 24, display: "24%", tone: "gold", sortOrder: 20 },
      { id: "35-44", label: "35–44", value: 6, display: "6%", tone: "gold", sortOrder: 30 },
      { id: "45", label: "45+", value: 2, display: "2%", tone: "gold", sortOrder: 40 },
    ],
    countries: [
      { id: "ar", label: "Argentina", value: 54, display: "54%", tone: "green", sortOrder: 10 },
      { id: "mx", label: "México", value: 15, display: "15%", tone: "gold", sortOrder: 20 },
      { id: "es", label: "España", value: 11, display: "11%", tone: "gold", sortOrder: 30 },
      { id: "cl", label: "Chile", value: 8, display: "8%", tone: "gold", sortOrder: 40 },
    ],
    gender: [
      { id: "women", value: "84%", label: "Mujeres", tone: "gold", sortOrder: 10 },
      { id: "men", value: "16%", label: "Hombres", tone: "gold", sortOrder: 20 },
    ],
    interests: [
      "Moda alternativa",
      "Beauty & skincare",
      "Lifestyle",
      "Joyería",
      "Música",
      "Arte visual",
      "Contenido dark / editorial",
    ],
  },
  visualWorld: {
    eyebrow: "04 — Portfolio visual",
    heading: "Estética y",
    headingAccent: "calidad de imagen.",
    images: [
      {
        id: "world-1",
        ...image(WORLD_1, "Desfile — look 2"),
        caption: "Pasarela · Actitud",
        sortOrder: 10,
      },
      {
        id: "world-2",
        ...image(WORLD_2, "Desfile — look 3"),
        caption: "Fashion · Silueta",
        sortOrder: 20,
      },
      {
        id: "world-3",
        ...image(WORLD_3, "Sesión editorial"),
        caption: "Detalle · Producto",
        sortOrder: 30,
      },
      {
        id: "world-4",
        ...image(WORLD_4, "Sesión editorial"),
        caption: "Editorial · Mood",
        sortOrder: 40,
      },
    ],
  },
  campaigns: {
    eyebrow: "05 — Ejemplos de contenido",
    heading: "Cómo se ve",
    headingAccent: "tu marca con ella.",
    intro:
      "Referencias de integración de producto (no casos de clientes confidenciales). Sirven para alinear el brief: tono, formato y objetivo de campaña.",
    concepts: [
      {
        id: "shadows",
        eyebrow: "Moda · Reel",
        title: "Juego de sombras",
        copy: "Producto en editorial 9:16 con luz dura. Ideal para lanzamiento de colección o drop.",
        tags: ["Reel 15–30s", "Awareness", "Stories"],
        image: image(CONCEPT_1, "Concepto desfile"),
        sortOrder: 10,
      },
      {
        id: "jewelry",
        eyebrow: "Joyería · UGC",
        title: "Piezas en primer plano",
        copy: "Detalle de producto protagonista. Apto orgánico y con derechos para ads de catálogo.",
        tags: ["UGC", "Reel", "Paid media"],
        image: image(CONCEPT_2, "Concepto sesión joyería"),
        sortOrder: 20,
      },
      {
        id: "skincare",
        eyebrow: "Beauty · Tutorial",
        title: "Ritual de producto",
        copy: "Integración natural de skincare (texturas, uso real). CTA o código si el brief lo pide.",
        tags: ["Consideration", "Stories", "Review"],
        image: image(CONCEPT_3, "Concepto skincare"),
        sortOrder: 30,
      },
      {
        id: "lifestyle",
        eyebrow: "Lifestyle · Collab",
        title: "Día con la marca",
        copy: "Storytelling cotidiano con inserción de marca sin forzar el tono de la creator.",
        tags: ["Collab", "Stories pack", "Always-on"],
        image: image(CONCEPT_4, "Concepto lifestyle"),
        sortOrder: 40,
      },
    ],
  },
  collaboration: {
    eyebrow: "06 — Servicios / entregables",
    heading: "Qué podés",
    headingAccent: "contratar.",
    intro:
      "Menú de servicios para marketing y agencias. Cada ítem es un entregable cotizable. La rate card se arma según objetivo, cantidad de piezas y uso (orgánico vs paid).",
    formats: [
      {
        id: "reel",
        title: "Reel / video corto",
        description:
          "1 pieza vertical 9:16 (15–45 s) para Instagram y/o TikTok. Alineada al brief, 1 ronda de feedback, subtítulos opcionales.",
        tags: ["Awareness", "Producto", "Launch"],
        sortOrder: 10,
      },
      {
        id: "stories",
        title: "Pack de Stories",
        description:
          "Secuencia de Stories (típ. 4–8) con mención, stickers y CTA. Ideal para promo, código o tráfico a link.",
        tags: ["Conversión", "Promo", "Código"],
        sortOrder: 20,
      },
      {
        id: "ugc",
        title: "UGC + derechos para ads",
        description:
          "Contenido en tono creator para uso de la marca en Meta/TikTok Ads. Plazo y territorios se definen en contrato.",
        tags: ["Paid media", "Always-on", "Catálogo"],
        sortOrder: 30,
      },
      {
        id: "photo",
        title: "Set de fotos",
        description:
          "Selección de fotos para feed, e-commerce o lookbook digital. Entrega en alta resolución + web.",
        tags: ["Feed", "E-commerce", "Lookbook"],
        sortOrder: 40,
      },
      {
        id: "bundle",
        title: "Paquete multi-pieza",
        description:
          "Combo Reel + Stories + foto/UGC en un solo brief. Pensado para lanzamientos o mes de contenido.",
        tags: ["Full funnel", "Mensual", "Ambassadorship"],
        sortOrder: 50,
      },
      {
        id: "event",
        title: "Evento / PR content",
        description:
          "Presencia o cobertura de evento de marca + recap en redes. Coordinación de horarios y dress code.",
        tags: ["PR", "Evento", "Recap"],
        sortOrder: 60,
      },
    ],
    notes: [
      "Brief en español · respuesta comercial en 24–48 h hábiles",
      "Entrega nativa: 9:16, Stories, feed",
      "Producción en Buenos Aires · envío de producto a coordinar",
      "Derechos paid media: cotización aparte (plazo + geo)",
      "Rate card y este media kit se actualizan a pedido del equipo de marca",
    ],
  },
  contact: {
    eyebrow: "07 — Contacto comercial",
    heading: "Enviá el brief.",
    headingAccent: "Cotizamos en 48 h.",
    body: "Incluí: objetivo (awareness, UGC ads, launch), plataformas, cantidad de piezas, fechas y si necesitás derechos paid. Respondemos con propuesta, tiempos y rate.",
  },
};
